import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { getAllBriefs, deleteBrief, updateBrief } from "../api/api";
import { normalizeBrief } from "../utils/normalizeBrief";
import toast from "react-hot-toast";
import "./Brief.css";
import FormGuidelines from "../components/FormGuidelines";

function getInitials(name = "") {
  return (name || "").split(" ").map(n => n[0]).join("").toUpperCase().slice(0, 2);
}

export default function Brief() {
  const navigate = useNavigate();
  const { currentUser } = useAuth();

  const [briefs, setBriefs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [editingBrief, setEditingBrief] = useState(null);
  const [editStatus, setEditStatus] = useState("NEW");
  const [showEditModal, setShowEditModal] = useState(false);

  const userRole = (currentUser?.role || "").toUpperCase();
  const isAdmin = userRole === "FOUNDER" || userRole === "TEAM_LEADER";

  useEffect(() => { fetchBriefs(); }, [page]);

  const fetchBriefs = async () => {
    try {
      setLoading(true);
      setError(null);
      const res = await getAllBriefs(page, 10);
      const data = res?.data?.data ?? res?.data ?? res?.briefs ?? [];
      setBriefs(Array.isArray(data) ? data.map(normalizeBrief) : []);
      setTotalPages(res?.data?.totalPages || res?.totalPages || 1);
    } catch {
      setError("تعذر تحميل البيانات");
    } finally {
      setLoading(false);
    }
  };

  const filtered = briefs.filter(b =>
    [b.name, b.email, b.company, b.phone].some(f =>
      (f || "").toLowerCase().includes(search.toLowerCase())
    )
  );

  const handleDelete = async (e, id) => {
    e.stopPropagation();
    if (!window.confirm("حذف هذا الـ Brief؟")) return;
    try {
      await deleteBrief(id);
      setBriefs(prev => prev.filter(b => b._id !== id));
      toast.success("Brief deleted successfully");
    } catch (err) {
      toast.error(err?.response?.data?.message || "فشل الحذف");
    }
  };

  const handleEditStatus = (e, brief) => {
    e.stopPropagation();
    setEditingBrief(brief);
    setEditStatus(brief.status || "NEW");
    setShowEditModal(true);
  };

  const handleUpdateStatus = async () => {
    try {
      await updateBrief(editingBrief._id, { status: editStatus });
      toast.success("Status updated successfully!");
      await fetchBriefs(); // Reload data from API to ensure consistency
      setShowEditModal(false);
      setEditingBrief(null);
    } catch (err) {
      toast.error(err?.response?.data?.message || "Failed to update status");
    }
  };

  if (loading) return (
    <div style={{ display:"flex", alignItems:"center", justifyContent:"center", minHeight:"60vh" }}>
      <div style={{ color:"#4b5563", fontSize:15 }}>جاري التحميل…</div>
    </div>
  );

  if (error) return (
    <div style={{ display:"flex", flexDirection:"column", alignItems:"center", justifyContent:"center", minHeight:"60vh", gap:12 }}>
      <div style={{ color:"#f87171", fontSize:15 }}>{error}</div>
      <button onClick={fetchBriefs} style={{ padding:"10px 24px", background:"#facc15", color:"#0a0f1a", border:"none", borderRadius:8, fontWeight:700, cursor:"pointer" }}>
        إعادة المحاولة
      </button>
    </div>
  );

  return (
    <div className="brief-page">

      {/* Header */}
      <div className="brief-header">
        <div>
          <h1>Brief</h1>
          <p>{briefs.length} briefs total</p>
        </div>
      </div>

      {/* Search */}
      <div className="search-bar">
        <span className="search-icon">🔍</span>
        <input
          placeholder="Search by name, email, phone or company…"
          value={search}
          onChange={e => setSearch(e.target.value)}
        />
      </div>

      {/* Table */}
      <div className="brief-table-wrapper">
        <table className="brief-table">
          <thead>
            <tr>
              <th>Client</th>
              <th>Email</th>
              <th>Phone</th>
              <th>Company</th>
              <th>Status</th>
              <th>Date</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {filtered.length === 0 ? (
              <tr><td colSpan={7}><div className="empty-state"><p>No briefs found.</p></div></td></tr>
            ) : (
              filtered.map(brief => (
                <tr key={brief._id} onClick={() => navigate(`/brief/${brief._id}`)}>
                  <td>
                    <div className="client-name-cell">
                      <div className="client-avatar">{getInitials(brief.name)}</div>
                      <div className="client-name-text">{brief.name || "—"}</div>
                    </div>
                  </td>
                  <td>{brief.email || "—"}</td>
                  <td>{brief.phone || "—"}</td>
                  <td>{brief.company || "—"}</td>
                  <td>
                    <span className={`status-badge ${brief.status?.toLowerCase() || "new"}`}>
                      {brief.status || "NEW"}
                    </span>
                  </td>
                  <td>{new Date(brief.createdAt).toLocaleDateString()}</td>
                  <td>
                    <div className="table-actions" onClick={e => e.stopPropagation()}>
                      <button className="action-btn" onClick={() => navigate(`/brief/${brief._id}`)}>
                        View
                      </button>
                      {isAdmin && (
                        <button className="action-btn edit-btn" onClick={e => handleEditStatus(e, brief)}>
                          Edit
                        </button>
                      )}
                      <button className="action-btn delete-btn" onClick={e => handleDelete(e, brief._id)}>
                        Delete
                      </button>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="pagination">
          <button 
            onClick={() => setPage(p => Math.max(1, p - 1))}
            disabled={page === 1}
            className="pagination-btn"
          >
            Previous
          </button>
          <span className="pagination-info">Page {page} of {totalPages}</span>
          <button 
            onClick={() => setPage(p => Math.min(totalPages, p + 1))}
            disabled={page === totalPages}
            className="pagination-btn"
          >
            Next
          </button>
        </div>
      )}

      {/* Edit Status Modal */}
      {showEditModal && (
        <div className="modal-overlay" onClick={() => setShowEditModal(false)}>
          <div className="modal-content" onClick={e => e.stopPropagation()}>
            <h2>Edit Breif Status</h2>
            <FormGuidelines />
            <div className="modal-form">
              <label>Select Status:</label>
              <select
                value={editStatus}
                onChange={e => setEditStatus(e.target.value)}
              >
                <option value="NEW">NEW</option>
                <option value="IN_REVIEW">IN_REVIEW</option>
                <option value="APPROVED">APPROVED</option>
                <option value="REJECTED">REJECTED</option>
              </select>
            </div>
            <div className="modal-actions">
              <button className="modal-btn cancel-btn" onClick={() => setShowEditModal(false)}>
                Cancel
              </button>
              <button className="modal-btn save-btn" onClick={handleUpdateStatus}>
                Save
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
